import axios, {AxiosResponse} from 'axios';
import moment from 'moment';

const loginLocalStorageKey = 'oauth_request_cache';
const clientId = 'analytics';

const axiosWithoutDefaults = axios.create();

const expirationThresholdSeconds = 30; // token expiry - threshold < current time then request new token
let oauthResponseCache: OAuthResponse | null = null;
let expiresIn: moment.Moment | undefined;
let refreshTokenPromise: Promise<string> | null = null;

interface OAuthResponse {
    access_token: string;
    token_type: string;
    refresh_token: string;
    expires_in: number; // in seconds
    score: string;
    userName?: string;
}

function cachedOAuthResponse(): OAuthResponse | null {
    if (oauthResponseCache !== null) {
        return oauthResponseCache;
    }
    const stringToken = localStorage.getItem(loginLocalStorageKey);
    if (stringToken) {
        oauthResponseCache = JSON.parse(stringToken);
        return oauthResponseCache;
    }
    return null;
}

export function getUserNameFromLocalStorageEntry(): string {
    const stringToken = localStorage.getItem(loginLocalStorageKey);
    if (stringToken) {
        const cachedResponse = JSON.parse(stringToken);
        if (cachedResponse.userName) {
            return cachedResponse.userName;
        }
    }
    return '';
}

function setCachedOAuthResponse(resp: OAuthResponse, userName?: string): void {
    const validUserName = userName === undefined ? getUserNameFromLocalStorageEntry() : userName;

    if (validUserName === '') {
        logout();
        return;
    }

    const oauthResponse = {...resp, userName: validUserName};
    oauthResponseCache = resp;
    localStorage.setItem(loginLocalStorageKey, JSON.stringify(oauthResponse));
    expiresIn = moment().add(oauthResponse.expires_in - expirationThresholdSeconds, 'seconds');
}

export function requestToken(username: string, password: string) {
    return axiosWithoutDefaults
        .get('oauth/token', {
            params: {
                username,
                password,
                grant_type: 'password',
                client_id: clientId,
            },
        })
        .then((resp: AxiosResponse<OAuthResponse>): string => {
            setCachedOAuthResponse(resp.data, username);
            logExpiresMessage();
            return resp.data.access_token;
        });
}

export function validateToken(): Promise<string> {
    const oauthResponse = cachedOAuthResponse();
    if (oauthResponse === null) {
        return Promise.reject(new Error('no cached oauthResponse'));
    }
    return axiosWithoutDefaults
        .get('oauth/check_token', {
            params: {
                token: oauthResponse.access_token,
            },
        })
        .then((resp: AxiosResponse<{exp: number}>) => {
            expiresIn = moment(resp.data.exp * 1000).subtract(expirationThresholdSeconds, 'seconds');
            logExpiresMessage();
            return oauthResponse.access_token;
        })
        .catch(refreshToken);
}

function refreshToken(): Promise<string> {
    const oauthResponse = cachedOAuthResponse();
    if (oauthResponse === null) {
        return Promise.reject(new Error('no cached oauthResponse'));
    }

    if (refreshTokenPromise) {
        return refreshTokenPromise;
    }

    console.log('requesting new authentication token');

    refreshTokenPromise = axiosWithoutDefaults
        .get('oauth/token', {
            params: {
                refresh_token: oauthResponse.refresh_token,
                client_id: clientId,
                grant_type: 'refresh_token',
            },
        })
        .then((resp) => {
            setCachedOAuthResponse(resp.data);
            logExpiresMessage();
            refreshTokenPromise = null;
            return resp.data.access_token;
        })
        .catch((err) => {
            refreshTokenPromise = null;
            logout();
            return Promise.reject(err);
        });
    return refreshTokenPromise;
}

export function token(): Promise<string> {
    if (moment().isAfter(expiresIn)) {
        return refreshToken();
    }
    const oauthResponse = cachedOAuthResponse();
    if (oauthResponse) {
        return Promise.resolve(oauthResponse.access_token);
    }
    return Promise.reject('no cached oauthResponse');
}

export function logout() {
    localStorage.removeItem(loginLocalStorageKey);
    expiresIn = undefined;
    oauthResponseCache = null;
}

function logExpiresMessage() {
    console.log(expiresIn ? 'token expires in ' + expiresIn.diff(moment(), 'seconds') + ' seconds' : 'token expire date missing');
}



// WEBPACK FOOTER //
// ./src/apis/TokenBasedLoginAPI.ts